import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import ProfileModal from '../components/ProfileModal';
import type { RouteHandle } from '../types';
import { LastWatchedExt, UserStatus } from '@shared/types';
import { userAtom, userLoginModalOpenAtom } from '../atoms/userAtom';
import { Spinner } from 'react-bootstrap';
import './MainLayout.scss';
import UserEvents from '../components/UserEvents';
import { getProgress } from '@app/utils/api';
import { isProgressLoading } from '@app/utils/progress';
import { collectShowIdentifiers } from '@shared/utils/show';
import { loadingProgressItemAtom, loadingShowIdsAtom } from '@app/atoms/uiAtoms';


const MainLayout = () => {
  const user = useAtomValue(userAtom);
  const [userLoginModalOpen, setUserLoginModalOpen] = useAtom(userLoginModalOpenAtom);
  const location = useLocation();
  const locationAuth: boolean = !!location.state?.auth;
  const [waiting, setWaiting] = useState(true);
  const [userEventsEnabled] = useState(false);
  const [loadingSeriesCount, setLoadingSeriesCount] = useState(0);
  const refreshInFlightRef = useRef(false);
  const setLoadingShowIds = useSetAtom(loadingShowIdsAtom);
  const setLoadingProgressItem = useSetAtom(loadingProgressItemAtom);

  const handle: RouteHandle = useMatches().at(-1)?.handle || {};
  const { pageId, auth = false } = handle;

  useEffect(() => {
    // Initially user is undefined, but is null or StoredUser after `requestAuthCheck()`
    // So the `undefined` means that the `auth/check` request is not yet completed
    setUserLoginModalOpen((auth || locationAuth) && user !== undefined && user?.status !== UserStatus.Active);
    setWaiting(auth && (!user || user.status !== UserStatus.Active));
  }, [user, auth, locationAuth, setUserLoginModalOpen]);

  useEffect(() => {
    if (!user || user.status !== UserStatus.Active) {
      setLoadingSeriesCount(0);
      setLoadingShowIds(new Set());
      setLoadingProgressItem(null);
      return;
    }

    let cancelled = false;
    let timerId: number | null = null;
    let hasActiveLoading = false;
    const ACTIVE_POLL_MS = 3000;
    const IDLE_POLL_MS = 10000;
    const scheduleNext = () => {
      if (cancelled) {
        return;
      }
      if (document.visibilityState !== 'visible') {
        timerId = window.setTimeout(scheduleNext, IDLE_POLL_MS);
        return;
      }
      timerId = window.setTimeout(() => {
        void refreshLoadingSeries();
      }, hasActiveLoading ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    };

    const refreshLoadingSeries = async () => {
      if (refreshInFlightRef.current) {
        return;
      }
      refreshInFlightRef.current = true;
      try {
        const resp = await getProgress(1, 40, { includeHidden: true });
        if (cancelled) {
          return;
        }
        // Enforce single-material loading UX: expose identifiers of the most recent loading item.
        let activeShowIds: string[] = [];
        let activeLoadingItem: LastWatchedExt | null = null;
        for (const item of resp.list || []) {
          if (!isProgressLoading(item)) {
            continue;
          }
          const ids = collectShowIdentifiers(item.show, [item.screenmediaId]);
          if (!ids.length) {
            continue;
          }
          activeShowIds = ids;
          activeLoadingItem = item;
          break;
        }
        const nextIds = new Set<string>(activeShowIds);
        hasActiveLoading = !!activeLoadingItem;
        setLoadingSeriesCount(activeLoadingItem ? 1 : 0);
        setLoadingShowIds(nextIds);
        setLoadingProgressItem(activeLoadingItem);
      } catch {
        if (!cancelled) {
          hasActiveLoading = false;
          setLoadingSeriesCount(0);
          setLoadingShowIds(new Set());
          setLoadingProgressItem(null);
        }
      } finally {
        refreshInFlightRef.current = false;
        scheduleNext();
      }
    };

    const handleFocusRefresh = () => {
      void refreshLoadingSeries();
    };
    const handleVisibilityRefresh = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      void refreshLoadingSeries();
    };

    void refreshLoadingSeries();
    window.addEventListener('focus', handleFocusRefresh);
    document.addEventListener('visibilitychange', handleVisibilityRefresh);

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearInterval(timerId);
      }
      window.removeEventListener('focus', handleFocusRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
    };
  }, [user, setLoadingShowIds, setLoadingProgressItem]);

  return (
    <div className="main-layout">
      <div className="tg-status-bar"></div>
      {userLoginModalOpen && <LoginModal />}
      <ProfileModal />
      <Header activeId={pageId} loadingSeriesCount={loadingSeriesCount} />
      <div className="d-flex flex-row flex-grow-1 overflow-hidden">
        <Sidebar pageId={pageId} />
        <div className="main-content flex-grow-1 overflow-auto">
        { waiting
          ? (<div className={`main-layout__waiting ${user === undefined ? 'animate' : ''}`}><Spinner animation="border" /></div>)
          : <Outlet context={{ pageId, auth } satisfies RouteHandle} /> }
        </div>
      </div>
      <Footer />
      {userEventsEnabled && <UserEvents />}
    </div>
  );
};

export default MainLayout;
